import React from 'react';
import PropTypes from 'prop-types';
import { FONTS } from '../constants/Fonts';
import { IMAGE_CONSTANTS } from '../constants/ImageConstans';
import { getPlayerPoints, getPlayerXp } from '../utils/utils';
import LeaderboardRoundScores from './LeaderboardRoundScores';
import './LeaderboardPodium.css';

/** Figma 18115:3097 / 3835 / 3838 — 72×72 medals */
const RANK_MEDALS = [
  IMAGE_CONSTANTS.V2_PODIUM_RANK_1,
  IMAGE_CONSTANTS.V2_PODIUM_RANK_2,
  IMAGE_CONSTANTS.V2_PODIUM_RANK_3,
];

const getPlayerFields = (user) => ({
  avatar:
    user?.avatar ||
    user?.profilePic ||
    user?.userImage ||
    IMAGE_CONSTANTS.V2_IMAGE_PLACEHOLDER,
  username: user?.username || user?.userName || user?.displayName || 'Player',
  points: getPlayerPoints(user),
  rapCoin: getPlayerXp(user),
  prizeTitle: user?.prizeTitle || '',
  prizeIcon: user?.prizeIcon || '',
});

/**
 * Figma 18115:3084 — arched pedestal card (top radius 90, flat bottom).
 * Winner (rank 1) is CENTER, elevated, with crown 18115:3787.
 */
const PodiumCard = ({ rank, user, tournamentScoring = false }) => {
  const { avatar, username, points, rapCoin, prizeTitle, prizeIcon } =
    getPlayerFields(user);
  const isFirst = rank === 1;
  const hasPrize = Boolean(prizeTitle || prizeIcon);

  return (
    <div
      className="lb-podium__card"
      data-rank={rank}
      data-node-id={
        rank === 1 ? '18115:3085' : rank === 2 ? '18115:3804' : '18115:3819'
      }
    >
      {isFirst ? (
        <div className="lb-podium__crown" data-node-id="18115:3787" aria-hidden="true">
          <img
            src={IMAGE_CONSTANTS.V2_PODIUM_CROWN}
            alt=""
            className="lb-podium__crown-img"
          />
        </div>
      ) : null}

      <div className="lb-podium__avatar-wrap" data-node-id="18115:3087">
        <img
          src={avatar}
          alt={username}
          className="lb-podium__avatar"
          data-node-id="18115:3088"
        />
        <img
          src={RANK_MEDALS[rank - 1]}
          alt={`Rank ${rank}`}
          className="lb-podium__medal"
          data-node-id={
            rank === 1 ? '18115:3097' : rank === 2 ? '18115:3835' : '18115:3838'
          }
        />
      </div>

      <div
        className="lb-podium__pedestal"
        data-node-id={
          rank === 1 ? '18115:3086' : rank === 2 ? '18115:3805' : '18115:3820'
        }
      >
        <p
          className="lb-podium__name"
          style={{ fontFamily: FONTS.INTER_BOLD }}
          data-node-id={
            rank === 1 ? '18115:3089' : rank === 2 ? '18115:3808' : '18115:3823'
          }
        >
          {username}
        </p>

        {tournamentScoring ? (
          <LeaderboardRoundScores
            player={user}
            fallbackIcon={IMAGE_CONSTANTS.V2_PODIUM_SCORE_IC}
            className="lb-podium__score"
          />
        ) : (
          <div className="lb-podium__score" data-node-id="18115:3090">
            <img
              src={IMAGE_CONSTANTS.V2_PODIUM_SCORE_IC}
              alt=""
              className="lb-podium__score-ic"
              data-node-id="18115:3091"
            />
            <span
              style={{ fontFamily: FONTS.INTER_BOLD }}
              data-node-id="18115:3092"
            >
              {points}
            </span>
          </div>
        )}

        <div
          className="lb-podium__won"
          data-node-id={
            rank === 1 ? '18115:3093' : rank === 2 ? '18115:3812' : '18115:3827'
          }
        >
          <span className="lb-podium__won-fill" aria-hidden="true" />
          <span className="lb-podium__won-inset" aria-hidden="true" />
          {tournamentScoring ? (
            <>
              <span className="lb-podium__coin-wrap" aria-hidden="true">
                <img
                  src={IMAGE_CONSTANTS.V2_PODIUM_SCORE_IC}
                  alt=""
                  className="lb-podium__won-score-ic"
                />
              </span>
              <span style={{ fontFamily: FONTS.INTER_MEDIUM }}>{points}</span>
            </>
          ) : (
            <>
              <span className="lb-podium__coin-wrap" data-node-id="18115:3095" aria-hidden="true">
                <img
                  src={hasPrize && prizeIcon ? prizeIcon : IMAGE_CONSTANTS.V2_XP_COIN}
                  alt=""
                  className={hasPrize && prizeIcon ? 'lb-podium__prize-ic' : 'lb-podium__coin'}
                />
              </span>
              <span style={{ fontFamily: FONTS.INTER_MEDIUM }}>
                {hasPrize ? prizeTitle || '—' : rapCoin}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

PodiumCard.propTypes = {
  rank: PropTypes.number.isRequired,
  user: PropTypes.object,
  tournamentScoring: PropTypes.bool,
};

/**
 * Top-3 podium — visual order 2 | 1 | 3 (first elevated + crown).
 * Figma 18115:3084 Header Content.
 */
const LeaderboardPodium = ({
  topThree = [],
  showEmpty = false,
  tournamentScoring = false,
}) => {
  const second = topThree[1];
  const first = topThree[0];
  const third = topThree[2];

  if (!first && !second && !third && !showEmpty) return null;

  const showFirework = Boolean(first || second || third);

  return (
    <div className="lb-podium" data-node-id="18115:3084">
      {showFirework ? (
        <img
          className="lb-podium__firework"
          src={IMAGE_CONSTANTS.V2_PODIUM_FIREWORK}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      ) : null}
      <div className="lb-podium__slot lb-podium__slot--side">
        {second ? (
          <PodiumCard rank={2} user={second} tournamentScoring={tournamentScoring} />
        ) : (
          <div className="lb-podium__empty" />
        )}
      </div>
      <div className="lb-podium__slot lb-podium__slot--center">
        {first ? (
          <PodiumCard rank={1} user={first} tournamentScoring={tournamentScoring} />
        ) : (
          <div className="lb-podium__empty" />
        )}
      </div>
      <div className="lb-podium__slot lb-podium__slot--side">
        {third ? (
          <PodiumCard rank={3} user={third} tournamentScoring={tournamentScoring} />
        ) : (
          <div className="lb-podium__empty" />
        )}
      </div>
    </div>
  );
};

LeaderboardPodium.propTypes = {
  topThree: PropTypes.array,
  showEmpty: PropTypes.bool,
  tournamentScoring: PropTypes.bool,
};

export default LeaderboardPodium;
